/**
 * HelicopterController - Input handling and control mapping
 * Manages keyboard, mouse, and gamepad inputs for helicopter control
 */

export interface ControlState {
  collective: number; // -1 to 1
  cyclicPitch: number; // -1 to 1
  cyclicRoll: number; // -1 to 1
  yaw: number; // -1 to 1
  fireWeapon: boolean;
  cycleWeapon: boolean;
  cycleView: boolean;
  designateTarget: boolean;
}

export class HelicopterController {
  private keys: Set<string> = new Set();
  private mouseX: number = 0;
  private mouseY: number = 0;
  private mouseButtons: Set<number> = new Set();
  private controlState: ControlState;
  private readonly canvasWidth: number;
  private readonly canvasHeight: number;

  constructor(canvasWidth: number = 1920, canvasHeight: number = 1080) {
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.controlState = {
      collective: 0,
      cyclicPitch: 0,
      cyclicRoll: 0,
      yaw: 0,
      fireWeapon: false,
      cycleWeapon: false,
      cycleView: false,
      designateTarget: false,
    };
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Keyboard events
    window.addEventListener('keydown', (e) => this.onKeyDown(e));
    window.addEventListener('keyup', (e) => this.onKeyUp(e));

    // Mouse events
    window.addEventListener('mousemove', (e) => this.onMouseMove(e));
    window.addEventListener('mousedown', (e) => this.onMouseDown(e));
    window.addEventListener('mouseup', (e) => this.onMouseUp(e));
  }

  private onKeyDown(event: KeyboardEvent): void {
    this.keys.add(event.code);
  }

  private onKeyUp(event: KeyboardEvent): void {
    this.keys.delete(event.code);
  }

  private onMouseMove(event: MouseEvent): void {
    this.mouseX = event.clientX;
    this.mouseY = event.clientY;
  }

  private onMouseDown(event: MouseEvent): void {
    this.mouseButtons.add(event.button);
  }

  private onMouseUp(event: MouseEvent): void {
    this.mouseButtons.delete(event.button);
  }

  /**
   * Update control state based on current inputs
   */
  update(): ControlState {
    // Reset transient states
    this.controlState.fireWeapon = false;
    this.controlState.cycleWeapon = false;
    this.controlState.cycleView = false;
    this.controlState.designateTarget = false;

    // Collective (W/S keys)
    this.controlState.collective = 0;
    if (this.keys.has('KeyW')) this.controlState.collective += 1;
    if (this.keys.has('KeyS')) this.controlState.collective -= 1;

    // Cyclic Roll (A/D keys)
    this.controlState.cyclicRoll = 0;
    if (this.keys.has('KeyA')) this.controlState.cyclicRoll -= 1;
    if (this.keys.has('KeyD')) this.controlState.cyclicRoll += 1;

    // Cyclic Pitch (Arrow keys)
    this.controlState.cyclicPitch = 0;
    if (this.keys.has('ArrowUp')) this.controlState.cyclicPitch -= 1;
    if (this.keys.has('ArrowDown')) this.controlState.cyclicPitch += 1;

    // Yaw (Q/E keys)
    this.controlState.yaw = 0;
    if (this.keys.has('KeyQ')) this.controlState.yaw -= 1;
    if (this.keys.has('KeyE')) this.controlState.yaw += 1;

    // Weapon controls
    if (this.keys.has('Space')) {
      this.controlState.fireWeapon = true;
    }

    if (this.keys.has('Tab')) {
      this.controlState.cycleWeapon = true;
      this.keys.delete('Tab'); // Prevent repeat
    }

    if (this.keys.has('F1') || this.keys.has('F2')) {
      this.controlState.cycleView = true;
      this.keys.delete('F1');
      this.keys.delete('F2');
    }

    if (this.keys.has('KeyT')) {
      this.controlState.designateTarget = true;
    }

    return { ...this.controlState };
  }

  /**
   * Get mouse position normalized to -1 to 1 range
   */
  getMousePosition(): { x: number; y: number } {
    const x = (this.mouseX / this.canvasWidth) * 2 - 1;
    const y = -(this.mouseY / this.canvasHeight) * 2 + 1;
    return { x, y };
  }

  /**
   * Check if a key is currently pressed
   */
  isKeyPressed(code: string): boolean {
    return this.keys.has(code);
  }

  /**
   * Check if a mouse button is currently pressed
   */
  isMouseButtonPressed(button: number): boolean {
    return this.mouseButtons.has(button);
  }

  /**
   * Get current control state
   */
  getControlState(): Readonly<ControlState> {
    return { ...this.controlState };
  }

  /**
   * Clean up event listeners
   */
  dispose(): void {
    // In a real implementation, we'd remove event listeners here
    // For simplicity, we're using arrow functions which makes removal harder
    // In production code, we'd store bound function references
  }
}
